import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";

/**
 * Admin-only Database Cleanup card.
 *
 * Calls POST /api/admin/cleanup-database in two passes:
 *   1. dry_run:true  → shows a preview of counts inside the confirm dialog.
 *   2. dry_run:false → actually deletes when the admin confirms.
 */
export default function DatabaseCleanupCard() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);        // { users, private_leagues, ... }
  const [lastResult, setLastResult] = useState(null);  // { deleted: { ... } } after execute

  const openDialog = async () => {
    setLastResult(null);
    setPreview(null);
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.post("/admin/cleanup-database", { dry_run: true });
      setPreview(data?.would_delete || {});
    } catch (e) {
      toast.error(formatApiError(e));
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/cleanup-database", { dry_run: false });
      setLastResult(data?.deleted || {});
      toast.success("Database cleanup complete.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const rows = [
    ["Users (@dugsy.app, @example.com, guest1@pub.co.uk)", "users"],
    ["Private leagues", "private_leagues"],
    ["Demo predictions", "demo_predictions"],
    ["Password reset tokens", "password_reset_tokens"],
    ["Mocked email events", "email_events_mocked"],
    ["Test Stripe transactions (cs_test_*)", "payment_transactions_test"],
  ];

  const totals = preview
    ? Object.values(preview).reduce((a, b) => a + Number(b || 0), 0)
    : 0;

  return (
    <section
      data-testid="db-cleanup-card"
      className="border border-zinc-200 rounded-sm bg-white p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-red-600" /> Danger zone
          </p>
          <h2 className="font-heading text-2xl font-semibold text-zinc-950 mt-1">
            Database cleanup.
          </h2>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            One-time scrub of bots and test data. Deletes QA users (
            <code className="text-xs">@dugsy.app</code>,{" "}
            <code className="text-xs">@example.com</code>,{" "}
            <code className="text-xs">guest1@pub.co.uk</code>), all private leagues,
            demo predictions, password reset tokens, mocked email events, and
            Stripe test transactions. All real users, matches, and historical
            league data are preserved.
          </p>
        </div>
        <Button
          type="button"
          variant="destructive"
          onClick={openDialog}
          data-testid="db-cleanup-open-btn"
          className="rounded-sm"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Cleanup database
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!loading) setOpen(v); }}>
        <DialogContent
          data-testid="db-cleanup-dialog"
          className="rounded-sm max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              This will permanently delete all test data. Are you sure?
            </DialogTitle>
            <DialogDescription>
              Review the counts below. Nothing has been deleted yet.
            </DialogDescription>
          </DialogHeader>

          {loading && !preview ? (
            <div className="flex items-center gap-2 text-sm text-zinc-600 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
            </div>
          ) : (
            <div className="mt-2">
              {preview && !lastResult && (
                <ul
                  className="text-sm text-zinc-800 divide-y divide-zinc-200 border border-zinc-200 rounded-sm"
                  data-testid="db-cleanup-preview-list"
                >
                  {rows.map(([label, key]) => (
                    <li
                      key={key}
                      className="flex items-center justify-between px-3 py-2"
                    >
                      <span className="text-zinc-700">{label}</span>
                      <span
                        className="font-mono tabular-nums"
                        data-testid={`db-cleanup-preview-${key}`}
                      >
                        {Number(preview[key] || 0)}
                      </span>
                    </li>
                  ))}
                  <li className="flex items-center justify-between px-3 py-2 bg-zinc-50 font-semibold">
                    <span>Total rows to delete</span>
                    <span
                      className="font-mono tabular-nums"
                      data-testid="db-cleanup-preview-total"
                    >
                      {totals}
                    </span>
                  </li>
                </ul>
              )}

              {lastResult && (
                <div
                  className="text-sm text-zinc-800 border border-emerald-200 bg-emerald-50 rounded-sm p-3"
                  data-testid="db-cleanup-result"
                >
                  <p className="font-semibold text-emerald-800 mb-2">
                    Deletion complete.
                  </p>
                  <ul className="space-y-1">
                    {rows.map(([label, key]) => (
                      <li
                        key={key}
                        className="flex items-center justify-between text-xs"
                      >
                        <span className="text-zinc-700">{label}</span>
                        <span
                          className="font-mono tabular-nums"
                          data-testid={`db-cleanup-result-${key}`}
                        >
                          {Number(lastResult[key] || 0)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
              data-testid="db-cleanup-cancel-btn"
              className="rounded-sm"
            >
              {lastResult ? "Close" : "Cancel"}
            </Button>
            {!lastResult && (
              <Button
                type="button"
                variant="destructive"
                onClick={execute}
                disabled={loading || !preview || totals === 0}
                data-testid="db-cleanup-confirm-btn"
                className="rounded-sm"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Yes, delete {totals} row{totals === 1 ? "" : "s"}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
